import type { ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

/*
 * The Input's own look, on a field that holds a paragraph. `field-sizing-content`
 * lets the box grow with what is typed into it, so a long section never turns
 * into a three-line scroll window — where a document is being written, the
 * document is what should be visible.
 */
const textareaVariants = cva(
  [
    'w-full font-medium outline-none',
    'placeholder:text-ink-muted disabled:opacity-40',
    'aria-invalid:border-danger-line aria-invalid:bg-danger-fill/50',
  ],
  {
    variants: {
      /*
       * Who decides how tall the box is.
       *
       * `content` is the default and the one to reach for: the box grows with
       * what is typed, so a long section is never a three-line scroll window.
       *
       * `manual` hands the height to the writer instead — a fixed opening
       * height and the browser's own grip. For a field on a long form, where a
       * box that grows while you type pushes the rest of the form down the page
       * as you work. The two cannot be combined: `field-sizing: content` keeps
       * overruling whatever the grip was dragged to.
       */
      sizing: {
        content: 'resize-none field-sizing-content',
        manual: 'resize-y',
      },
      variant: {
        quiet:
          'bg-fill-faint border border-transparent rounded-md text-ink transition focus:shadow-focus',
        bare: 'bg-transparent border-none text-ink',
      },
      fieldSize: {
        md: 'text-base px-3.25 py-2.75',
        lg: 'text-base px-4 py-3',
      },
    },
    compoundVariants: [{ variant: 'bare', class: 'p-0' }],
    defaultVariants: { variant: 'quiet', fieldSize: 'md', sizing: 'content' },
  },
);

type TextareaVariants = VariantProps<typeof textareaVariants>;

interface TextareaProps
  extends
    Omit<ComponentPropsWithRef<'textarea'>, 'style' | 'className'>,
    TextareaVariants {
  className?: string;
  invalid?: boolean;
}

/** A field for text that runs to more than a line: a note, a section, a paragraph. */
function Textarea({
  className,
  variant,
  fieldSize,
  sizing,
  invalid,
  rows = 3,
  ...props
}: TextareaProps) {
  return (
    <textarea
      data-slot='textarea'
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(textareaVariants({ variant, fieldSize, sizing }), className)}
      {...props}
    />
  );
}

export { Textarea, textareaVariants };
export type { TextareaProps, TextareaVariants };
